import { Injectable } from '@jack-henry/frontend-utils/di';
import { deepEquals } from '@jack-henry/frontend-utils/functions';
import {
    AccountAccessModelDto,
    CompaniesClient,
    CompanyUserClient,
    SaveUserAccountAccessModelDto,
    SearchCompanyUserModelDto,
    UserInformationModelDto,
    UserSummaryModelDto,
} from '@treasury/api/bo';
import { CompanyService } from '@treasury/domain/backoffice/services/company/company.service';
import { LocalStorageService } from '@treasury/utils';

import { UsersService } from '../../../backoffice/services/users/users-service';
import {
    ProductFeatureEntitlementTypes,
    ProductFeatureLimitTypes,
} from '../../product-features/types';
import {
    CompanyUserAuthSuspension,
    CompanyUserChallengeDetails,
    CompanyUserInformation,
    CompanyUserProductFeatureSummary,
    CompanyUserState,
    CompanyUserSummary,
} from '../entities';
import {
    AccountAccess,
    CompanyUserAccountAccess,
    CompanyUserIpRestrictions,
    CompanyUserTimeAccess,
    IpRestrictionAddress,
} from '../entities/access';
import {
    CompanyUserAchEntitlements,
    CompanyUserProductEntitlements,
} from '../entities/entitlements';
import { CompanyUserAchLimits, CompanyUserProductLimits } from '../entities/limits';

export interface RecentlyViewedUser {
    id: number;
    timestamp: string;
}
export interface RecentlyViewedUsers {
    [companyId: number]: RecentlyViewedUser[];
}

const MAX_USERS = 5;

@Injectable()
export class BoCompanyUserHelper {
    constructor(
        private readonly companiesClient: CompaniesClient,
        private readonly companyUserClient: CompanyUserClient,
        private readonly localStorageService: LocalStorageService,
        private readonly companyService: CompanyService,
        private readonly usersService: UsersService
    ) {}

    /**
     * Retrieves the product feature summary for a specific company user.
     *
     * @param companyId - The unique identifier of the company.
     * @param userId - The unique identifier of the user within the company.
     * @returns A promise that resolves to an instance of {@link CompanyUserProductFeatureSummary}
     *          containing the product feature summary for the specified user.
     * @throws Will throw an error if the request to fetch product feature summary fails.
     */
    public async getCompanyUserProductFeatureSummary(companyId: number, userId: number) {
        const response = await this.companyUserClient.companyUserGetProductFeatureSummary(
            companyId,
            userId
        );
        return new CompanyUserProductFeatureSummary(response.data);
    }

    /**
     * Sends an enrollment email to a specific company user.
     *
     * @param companyId - The unique identifier of the company.
     * @param userId - The unique identifier of the user within the company.
     * @returns A promise that resolves to the API response data after sending the enrollment email.
     * @throws Will throw an error if the request to send the enrollment email fails.
     */
    public sendEnrollmentEmail(companyId: number, userId: number) {
        this.companyUserClient.companyUserGenerateEnrollmentEmail(companyId, userId);
    }

    /**
     * Retrieves the account access for a specific company user.
     *
     * @param companyId - The unique identifier of the company.
     * @param userId - The unique identifier of the user within the company.
     * @returns A promise that resolves to an instance of {@link CompanyUserAccountAccess}
     *          containing the account access for the specified user.
     * @throws Will throw an error if the request to fetch account access fails.
     */
    public async getCompanyUserAccountAccess(companyId: number, userId: number) {
        const response = await this.companyUserClient.companyUserGetAccountAccess(
            companyId,
            userId
        );
        return new CompanyUserAccountAccess(response.data);
    }

    /**
     * Retrieves the product limits for a specific company user by type.
     *
     * @param companyId - The unique identifier of the company.
     * @param userId - The unique identifier of the user within the company.
     * @param type - The type of product feature to retrieve limits for.
     * @returns A promise that resolves to an instance of {@link CompanyUserProductLimits}
     *          containing the product limits for the specified user.
     * @throws Will throw an error if the request to fetch product limits fails.
     */
    public async getCompanyUserProductLimitsByType(
        companyId: number,
        userId: number,
        type: ProductFeatureLimitTypes
    ) {
        const response = await this.companyUserClient.companyUserGetUserProductLimits(
            companyId,
            userId,
            { productFeatureType: type }
        );
        return new CompanyUserProductLimits(response.data);
    }

    /**
     * Retrieves the product entitlements for a specific company user by type.
     *
     * @param companyId - The unique identifier of the company.
     * @param userId - The unique identifier of the user within the company.
     * @param type - The type of product feature to retrieve entitlements for.
     * @returns A promise that resolves to an instance of {@link CompanyUserProductEntitlements}
     *          containing the product entitlements for the specified user.
     * @throws Will throw an error if the request to fetch product entitlements fails.
     */
    public async getCompanyUserProductEntitlementsByType(
        companyId: number,
        userId: number,
        type: ProductFeatureEntitlementTypes
    ) {
        const response = await this.companyUserClient.companyUserGetUserProductEntitlements(
            companyId,
            userId,
            { productFeatureType: type }
        );
        return new CompanyUserProductEntitlements(response.data);
    }

    /**
     * Retrieves the ACH limits for a specific company user.
     *
     * @param companyId - The unique identifier of the company.
     * @param userId - The unique identifier of the user within the company.
     * @returns A promise that resolves to an instance of {@link CompanyUserAchLimits}
     *          containing the ACH limits for the specified user.
     * @throws Will throw an error if the request to fetch ACH limits fails.
     */
    public async getCompanyUserAchLimits(companyId: number, userId: number) {
        const response = await this.companyUserClient.companyUserGetUserAchLimits(
            companyId,
            userId
        );
        return new CompanyUserAchLimits(response.data);
    }

    /**
     * Retrieves the ACH entitlements for a specific company user.
     *
     * @param companyId - The unique identifier of the company.
     * @param userId - The unique identifier of the user within the company.
     * @returns A promise that resolves to an instance of {@link CompanyUserAchEntitlements}
     *          containing the ACH entitlements for the specified user.
     * @throws Will throw an error if the request to fetch ACH entitlements fails.
     */
    public async getCompanyUserAchEntitlements(companyId: number, userId: number) {
        const response = await this.companyUserClient.companyUserGetUserAchEntitlements(
            companyId,
            userId
        );
        return new CompanyUserAchEntitlements(response.data);
    }

    /**
     * Approves a company user.
     *
     * @param companyId - The unique identifier of the company.
     * @param userId - The unique identifier of the user within the company.
     * @param comments - The comments to include with the approval.
     * @returns A promise that resolves to the API response data after approving the user.
     */
    public async approveCompanyUser(companyId: number, userId: number, comments: string) {
        const response = await this.companyUserClient.companyUserApprove(companyId, userId, {
            comments,
        });
        return response.data;
    }

    /**
     * Adds access to accounts for a specific company user.
     *
     * @param companyId - The unique identifier of the company.
     * @param userId - The unique identifier of the user within the company.
     * @param accounts - The accounts to add access to.
     * @returns A promise that resolves to the API response data after adding the access to the accounts.
     */
    public async addCompanyUserAccessToAccounts(
        companyId: number,
        userId: number,
        accounts: AccountAccess[]
    ) {
        const body: SaveUserAccountAccessModelDto = {
            addedAccounts: accounts.map(a => a.toDto()) as AccountAccessModelDto[],
            removedAccounts: [],
        };

        const response = await this.companyUserClient.companyUserSaveUserAccountAccess(
            companyId,
            userId,
            body
        );
        return response.data;
    }

    /**
     * Adds access to accounts for a specific company user.
     *
     * @param companyId - The unique identifier of the company.
     * @param userId - The unique identifier of the user within the company.
     * @param accounts - The accounts to add access to.
     * @returns A promise that resolves to the API response data after adding the access to the accounts.
     */
    public async removeCompanyUserAccessToAccounts(
        companyId: number,
        userId: number,
        accounts: AccountAccess[]
    ) {
        const body: SaveUserAccountAccessModelDto = {
            addedAccounts: [],
            removedAccounts: accounts.map(a => a.toDto()) as AccountAccessModelDto[],
        };
        const response = await this.companyUserClient.companyUserSaveUserAccountAccess(
            companyId,
            userId,
            body
        );
        return response.data;
    }

    /**
     * Rejects a company user.
     *
     * @param companyId - The unique identifier of the company.
     * @param userId - The unique identifier of the user within the company.
     * @param comments - The comments to include with the rejection.
     * @returns A promise that resolves to the API response data after rejecting the user.
     */
    public async rejectCompanyUser(companyId: number, userId: number, comments: string) {
        const response = await this.companyUserClient.companyUserReject(companyId, userId, {
            comments,
        });
        return response.data;
    }

    /**
     * Retrieves IP Restriction details for a specific company user.
     *
     * @param companyId - The unique identifier of the company.
     * @param userId - The unique identifier of the user within the company.
     * @returns A promise that resolves to an object containing a boolean of`hasPendingChanges`and {@link CompanyUserInformation}
     *          containing the user's basic information.
     * @throws Will throw an error if the request to fetch challenge details fails.
     */
    public async getCompanyUserInformation(companyId: number, userId: number) {
        const { pendingUser, user } = (
            await this.companyUserClient.companyUserGetUserInformation(companyId, userId)
        ).data;

        const hasPendingChanges = !deepEquals(pendingUser, user);

        return {
            hasPendingChanges,
            user: new CompanyUserInformation(hasPendingChanges ? pendingUser : user),
        };
    }

    /**
     * Retrieves the state information for a specific company user.
     *
     * @param {number} companyId - The unique identifier of the company.
     * @param {number} userId - The unique identifier of the user within the company.
     * @returns {Promise<CompanyUserState>} A promise that resolves to an instance of {@link CompanyUserState}
     *          containing the user's state information.
     * @throws {Error} Will throw an error if the request to fetch user state fails.
     */
    public async getCompanyUserState(companyId: number, userId: number): Promise<CompanyUserState> {
        const response = await this.companyUserClient.companyUserGetUserState(companyId, userId);
        return new CompanyUserState(response.data);
    }

    /**
     * Deletes a company user.
     *
     * @param companyId - The unique identifier of the company.
     * @param userId - The unique identifier of the user within the company.
     * @returns A promise that resolves to a boolean indicating whether the user was deleted.
     * @throws Will throw an error if the request to delete the user fails.
     */
    public async deleteCompanyUser(companyId: number, userId: number) {
        const response = await this.companyUserClient.companyUserDeleteUser(companyId, userId);
        return response.data;
    }

    public async setActive(
        companyId: number,
        userId: number,
        isActive: boolean,
        comments?: string
    ) {
        const user = (
            await this.companyUserClient.companyUserSetActive(companyId, userId, {
                isActive,
                // eslint-disable-next-line no-restricted-syntax
                comments: comments || null,
            })
        ).data;
        return new CompanyUserSummary(user);
    }

    /**
     * Discards user information that is pending submission for a specific company user.
     *
     * @param companyId - The unique identifier of the company.
     * @param userId - The unique identifier of the user within the company.
     * @param comments - Comments to include with the approval.
     * @returns A promise that resolves to a boolean indicating whether the approval was successful.
     * @throws Will throw an error if the request to discard the user details fail.
     */
    public async discardCompanyUserChanges(companyId: number, userId: number, comments: string) {
        const response = await this.companyUserClient.companyUserDiscard(companyId, userId, {
            comments: comments,
        });

        return response.data;
    }

    /**
     * Retrieves IP Restriction details for a specific company user.
     *
     * @param companyId - The unique identifier of the company.
     * @param userId - The unique identifier of the user within the company.
     * @returns A promise that resolves to an instance of `CompanyUserIpRestrictions`
     *          containing the ip restriction details for the specified user.
     * @throws Will throw an error if the request to fetch challenge details fails.
     */
    public async getCompanyUserIpRestrictions(companyId: number, userId: number) {
        const response = await this.companyUserClient.companyUserGetIpRestrictions(
            companyId,
            userId
        );
        return new CompanyUserIpRestrictions(response.data);
    }

    /**
     * Retrieves Time Access Restriction details for a specific company user.
     *
     * @param companyId - The unique identifier of the company.
     * @param userId - The unique identifier of the user within the company.
     * @returns A promise that resolves to an instance of `CompanyUserTimeAccess`
     *          containing the ip restriction details for the specified user.
     * @throws Will throw an error if the request to fetch challenge details fails.
     */
    public async getCompanyUserTimeAccessRestrictions(companyId: number, userId: number) {
        const response = await this.companyUserClient.companyUserGetUserTimeAccess(
            companyId,
            userId
        );
        return new CompanyUserTimeAccess(response.data);
    }

    /**
     *
     * @param companyId - The unique identifier of the company.
     * @param userId - The unique identifier of the user within the company.
     * @param ipAddresses - The IP addresses to save.
     * @param restricted - Whether the IP addresses are restricted.
     * @returns A promise that resolves to the API response data after saving the IP restrictions.
     * @throws Will throw an error if the request to save IP restrictions fails.
     */
    public async saveCompanyUserIpRestrictions(
        companyId: number,
        userId: number,
        ipAddresses: IpRestrictionAddress[],
        restricted: boolean
    ) {
        const ipAddressDtos = ipAddresses.map(ria => ria.toDto());

        const response = await this.companyUserClient.companyUserPutIpRestrictions(
            companyId,
            userId,
            { restricted: restricted },
            ipAddressDtos
        );

        return response.data;
    }

    /**
     * Saves time access restrictions for a specific company user.
     *
     * This method updates the time-based access restrictions for a user within a company.
     * It converts the time access configuration to DTO format and sends it to the API.
     *
     * @param {number} companyId - The unique identifier of the company.
     * @param {number} userId - The unique identifier of the user within the company.
     * @param {CompanyUserTimeAccess} timeAccess - The time access restrictions configuration to save.
     * @returns {Promise<any>} A promise that resolves to the API response data after saving the time access restrictions.
     * @throws {Error} Will throw an error if the request to save time access restrictions fails.
     */
    public async saveCompanyUserTimeAccessRestrictions(
        companyId: number,
        userId: number,
        timeAccess: CompanyUserTimeAccess
    ) {
        const response = await this.companyUserClient.companyUserSaveUserTimeAccess(
            companyId,
            userId,
            timeAccess.toDto()
        );
        return response.data;
    }

    /**
     * Retrieves the challenge details for a specific company user.
     *
     * @param companyId - The unique identifier of the company.
     * @param userId - The unique identifier of the user within the company.
     * @returns A promise that resolves to an instance of `CompanyUserChallengeDetails`
     *          containing the challenge details for the specified user.
     * @throws Will throw an error if the request to fetch challenge details fails.
     */
    public async getCompanyUserChallengeDetails(companyId: number, userId: number) {
        const response = await this.companyUserClient.companyUserGetUserChallengeDetail(
            companyId,
            userId
        );

        return new CompanyUserChallengeDetails(response.data);
    }

    /**
     * Resets the authentication details for a specific user within a company.
     *
     * @param companyId - The unique identifier of the company.
     * @param userId - The unique identifier of the user whose authentication details need to be reset.
     * @returns A promise that resolves to an instance of `CompanyUserChallengeDetails` containing the user's challenge details.
     * @throws An error if the operation fails or the user details cannot be retrieved.
     */
    public async resetUserAuthentication(companyId: number, userId: number) {
        const response = await this.companyUserClient.companyUserGetUserChallengeDetail(
            companyId,
            userId
        );

        return new CompanyUserChallengeDetails(response.data);
    }

    /**
     * Unlocks a company user by initiating an unlock user challenge.
     *
     * @param companyId - The unique identifier of the company.
     * @param userId - The unique identifier of the user to be unlocked.
     * @param duration - A number representing how long the user should be locked.
     * @returns A promise that resolves when the suspension operation is completed.
     */
    public async suspendUserAuthentication(companyId: number, userId: number, duration: number) {
        const response = await this.companiesClient.companiesSuspendUserAuthentication(
            companyId,
            userId,
            { challengeSuspensionDuration: duration }
        );

        return new CompanyUserAuthSuspension(response.data);
    }

    /**
     * Unlocks a company user by initiating an unlock user challenge.
     *
     * @param companyId - The unique identifier of the company.
     * @param userId - The unique identifier of the user to be unlocked.
     * @returns A promise that resolves when the restore operation is completed.
     */
    public async restoreUserAuthentication(companyId: number, userId: number) {
        const response = await this.companiesClient.companiesReenableUserAuthentication(
            companyId,
            userId
        );

        return new CompanyUserAuthSuspension(response.data);
    }

    /**
     * Unlocks a company user by initiating an unlock user challenge.
     *
     * @param companyId - The unique identifier of the company.
     * @param userId - The unique identifier of the user to be unlocked.
     * @param recollect - Optional flag indicating whether to prompt the user for new 2fa credentials the next time they login.
     * @returns A promise that resolves when the unlock operation is completed.
     */
    public async unlockCompanyUser(companyId: number, userId: number, recollect: boolean = false) {
        const response = await this.companyUserClient.companyUserUnlockUserChallenge(
            companyId,
            userId,
            { recollect }
        );
        return response.data;
    }

    /**
     * Saves a user to the recently viewed users list for a given company.
     *
     * This method updates the list of recently viewed users for a specified company by adding
     * the provided user to the beginning of the list. If the user already exists in the list,
     * it is moved to the beginning. If the list exceeds MAX_USERS, the oldest user is removed.
     *
     * @param companyId - The ID of the company.
     * @param user - The user to be added to the recently viewed users list.
     */
    public async saveUserToRecentlyViewedUser(companyId: number, userId: number) {
        const recentlyViewed = this.getRecentlyViewedUsersByCompanyId(companyId)?.filter(
            user => user.id !== userId
        );
        if (!recentlyViewed || recentlyViewed.length < 1) {
            this.setRecentlyViewedCompanyUsers(companyId, [
                { id: userId, timestamp: new Date().toISOString() },
            ]);
            return;
        }

        if (recentlyViewed) {
            if (recentlyViewed.length > MAX_USERS - 1) {
                recentlyViewed.pop();
            }
            recentlyViewed.unshift({ id: userId, timestamp: new Date().toISOString() });
            this.setRecentlyViewedCompanyUsers(companyId, recentlyViewed);
        }
    }

    /**
     * Retrieves the list of recently viewed users for a given company.
     *
     * @param {number} companyId - The ID of the company.
     * @returns {RecentlyViewedUsers[]} An array of recently viewed users for the specified company.
     */
    public async getRecentlyViewedCompanyUsers(companyId: number) {
        const recentlyViewedUsers = (this.getRecentlyViewedUsersByCompanyId(companyId) ?? [])
            .sort((a, b) => new Date(b.timestamp).getTime() - new Date(a.timestamp).getTime())
            .slice(0, MAX_USERS); // Ensure if more than MAX_USERS was previously stored, we only get MAX_USERS.
        const users = await this.usersService.getCompanyUsers(companyId, false);
        const filtered = recentlyViewedUsers
            .map(rvu => {
                const user = users.find(u => u.id === rvu.id);
                if (user) {
                    user.lastViewed = rvu.timestamp;
                }
                return user;
            })
            .filter(u => u !== undefined);
        return filtered;
    }

    public async saveCompanyUserInformation(
        companyId: number,
        userId: number,
        user: CompanyUserInformation
    ) {
        const response = await this.companyUserClient.companyUserSaveUserInformation(
            companyId,
            userId,
            user.toDto() as UserInformationModelDto
        );

        return response.data;
    }

    public async getCompanyUsers(
        companyId: number,
        onlyEnrolledAndActive = false
    ): Promise<CompanyUserSummary[]> {
        return this.usersService.getCompanyUsers(companyId, onlyEnrolledAndActive);
    }

    public async searchCompanyUsers(
        companyId: number,
        params: Omit<SearchCompanyUserModelDto, 'companyId'> = {
            activeUsersOnly: true,
            enrolledUsersOnly: true,
            includeRoles: true,
        }
    ): Promise<CompanyUserSummary[]> {
        const { activeUsersOnly, enrolledUsersOnly, includeRoles } = params;
        return (
            await this.companyUserClient.companyUserSearch2({
                activeUsersOnly,
                companyId,
                enrolledUsersOnly,
                includeRoles,
            })
        ).data.map((user: UserSummaryModelDto) => new CompanyUserSummary(user));
    }

    public async getAllCompanyUsers(companyId: number): Promise<CompanyUserSummary[]> {
        const recentlyViewedUsers = (await this.getRecentlyViewedUsersByCompanyId(companyId)) ?? [];
        const companyUsers = await this.getCompanyUsers(companyId, false);
        return companyUsers.map(user => {
            const recentUser = recentlyViewedUsers.find(u => u.id === user.id);
            if (recentUser) {
                user.lastViewed = recentUser.timestamp;
            }
            return user;
        });
    }

    private getRecentlyViewedUsersByCompanyId(companyId: number) {
        const recentlyViewed =
            this.localStorageService.get<RecentlyViewedUsers>('recentlyViewedUsers');
        return recentlyViewed ? recentlyViewed[companyId] : [];
    }

    private setRecentlyViewedCompanyUsers(companyId: number, users: RecentlyViewedUser[]) {
        this.localStorageService.set('recentlyViewedUsers', {
            ...this.localStorageService.get<RecentlyViewedUsers>('recentlyViewedUsers'),
            [companyId]: users,
        });
    }
}
